/**
 * Maps between the VENDOR_PROFILE read model and the VendorProfileUpdateInput
 * (used by `updateProfile`). `registerNewShop` is required and is always sent
 * as `false` here — we only ever EDIT the existing shop.
 */

/** Normalise an array-or-comma-string field into a string array. */
const toStringArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
  }
  return [];
};

export function profileToForm(profile) {
  const p = profile || {};
  return {
    businessName: p.businessName || '',
    businessDescription: p.businessDescription || '',
    businessEmail: p.businessEmail || '',
    businessPhone: p.businessPhone || '',
    street1: p.street1 || '',
    street2: p.street2 || '',
    city: p.city || '',
    state: p.state || '',
    postalCode: p.postalCode || '',
    country: p.country || '',
    openTime: p.openTime || '',
    closeTime: p.closeTime || '',
    gstRegistrationNumber: p.gst || '',
    fssaiLicenseNumber: '',
    vendorType: p.vendorType || '',
    businessType: toStringArray(p.businessType),
    // Day enum values are lowercase (monday, tuesday…); normalise to match.
    daysActive: toStringArray(p.daysActive).map((day) => day.toLowerCase()),
    preferredLanguage: p.preferredLanguage || '',
    vendorProfilePic: p.profilePic || '',
    licenseUrl: p.licenseUrl || '',
    menuUrl: p.menuUrl || '',
    isOnline: Boolean(p.isOnline),
    autoAcceptOrders: Boolean(p.autoAcceptOrders),
    acceptsNotifications: p.acceptsNotifications ?? true,
    isUseOtp: Boolean(p.isUseOtp),
  };
}

/**
 * Initial values for the "Add shop" form: carry over the active shop's
 * identity/branding (name, description, vendor type, business types, language,
 * open days, preferences) but leave contact, location, timings and compliance
 * blank — the new shop enters those fresh.
 */
export function addShopInitialValues(activeProfile) {
  return {
    ...profileToForm(activeProfile),
    businessEmail: '',
    businessPhone: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    postalCode: '',
    openTime: '',
    closeTime: '',
    gstRegistrationNumber: '',
    fssaiLicenseNumber: '',
    licenseUrl: '',
    menuUrl: '',
    // Sensible defaults for a new shop.
    isUseOtp: true,
    autoAcceptOrders: true,
    isOnline: true,
    acceptsNotifications: true,
  };
}

const trimmedOrUndefined = (value) => {
  const text = (value ?? '').toString().trim();
  return text === '' ? undefined : text;
};

const omitUndefined = (object) =>
  Object.fromEntries(Object.entries(object).filter(([, value]) => value !== undefined));

export function buildVendorProfileInput(form, { isNew = false } = {}) {
  // Empty text fields are omitted (not sent as "") so we never accidentally
  // clear existing data; booleans are always sent since they're explicit toggles.
  // `registerNewShop` is true ONLY when creating a brand-new shop.
  return omitUndefined({
    registerNewShop: Boolean(isNew),
    businessName: trimmedOrUndefined(form.businessName),
    businessDescription: trimmedOrUndefined(form.businessDescription),
    businessEmail: trimmedOrUndefined(form.businessEmail),
    businessPhone: trimmedOrUndefined(form.businessPhone),
    street1: trimmedOrUndefined(form.street1),
    street2: trimmedOrUndefined(form.street2),
    city: trimmedOrUndefined(form.city),
    state: trimmedOrUndefined(form.state),
    postalCode: trimmedOrUndefined(form.postalCode),
    country: trimmedOrUndefined(form.country),
    openTime: trimmedOrUndefined(form.openTime),
    closeTime: trimmedOrUndefined(form.closeTime),
    gstRegistrationNumber: trimmedOrUndefined(form.gstRegistrationNumber),
    fssaiLicenseNumber: trimmedOrUndefined(form.fssaiLicenseNumber),
    vendorProfilePic: trimmedOrUndefined(form.vendorProfilePic),
    licenseUrl: trimmedOrUndefined(form.licenseUrl),
    menuUrl: trimmedOrUndefined(form.menuUrl),
    vendorType: trimmedOrUndefined(form.vendorType),
    businessType: form.businessType?.length ? form.businessType : undefined,
    daysActive: form.daysActive?.length ? form.daysActive : undefined,
    preferredLanguage: trimmedOrUndefined(form.preferredLanguage),
    isOnline: Boolean(form.isOnline),
    autoAcceptOrders: Boolean(form.autoAcceptOrders),
    acceptsNotifications: Boolean(form.acceptsNotifications),
    isUseOtp: Boolean(form.isUseOtp),
  });
}
